/**
 * SINGLE SOURCE OF TRUTH FOR SEO METADATA
 * ---------------------------------------
 * Edit titles / descriptions here (id = Bahasa Indonesia, en = English).
 * The same data is used for:
 *   - the tags written into every prerendered HTML file at build time
 *   - the tags updated in the browser on client-side navigation
 *   - sitemap.xml (generated at build time from PAGES)
 *
 * Keep titles ~50-60 characters and descriptions ~120-160 characters.
 * After editing, run `npm run build` (Vercel does this on every deploy).
 */

export const SITE = {
  url: "https://titancontainers.id",
  name: "TITAN Containers",
  locales: ["id", "en"],
  // Locale used for the hreflang "x-default" (same as the "/" redirect fallback).
  defaultLocale: "en",
  ogLocale: { id: "id_ID", en: "en_US" },
  ogImage:
    "https://res.cloudinary.com/dt1jmjt25/image/upload/f_auto,q_auto,w_1200/v1761237549/Bagian_rent_qvhskt.jpg",
  logo: "https://titancontainers.id/titan_logo.svg",
  organization: {
    telephone: "+62-21-4064-4888",
    email: "info@titancontainers.id",
    address: {
      streetAddress:
        "The Kensington Office Tower, 10th Floor - Unit F, Boulevard Raya Road No. 1",
      addressLocality: "Kelapa Gading, North Jakarta",
      postalCode: "14240",
      addressCountry: "ID",
    },
  },
};

/**
 * One entry per indexable page.
 *  - path: URL path after /{lang}/ ("" = home page)
 *  - prerender: true  -> a real HTML file is generated at build time
 *  - noindex: true    -> <meta name="robots" content="noindex">
 *  - product: adds Product JSON-LD (name/description/image)
 */
export const PAGES = [
  {
    key: "home",
    path: "",
    prerender: true,
    title: {
      id: "TITAN Containers | Cold Storage Portabel untuk Beli & Sewa",
      en: "TITAN Containers | Portable Cold Storage to Buy & Rent",
    },
    description: {
      id: "Solusi cold storage portabel TITAN Containers: beli atau sewa unit penyimpanan dingin fleksibel hingga -70°C untuk pangan, farmasi, dan industri di seluruh Indonesia.",
      en: "TITAN Containers portable cold storage: buy or rent flexible refrigerated units down to -70°C for food, pharma, and industry across Indonesia.",
    },
  },
  {
    key: "about",
    path: "about",
    prerender: true,
    title: {
      id: "Tentang Kami | TITAN Containers",
      en: "About Us | TITAN Containers",
    },
    description: {
      id: "Kenali TITAN Containers: teknologi, keahlian rantai dingin global, dan desain yang mengutamakan fungsi, didistribusikan di Indonesia oleh PT DS Solutions International.",
      en: "Meet TITAN Containers: technology, global cold chain expertise and design-first thinking, distributed in Indonesia by PT DS Solutions International.",
    },
  },
  {
    key: "products",
    path: "product",
    prerender: true,
    title: {
      id: "Produk Cold Storage Portabel | TITAN Containers",
      en: "Portable Cold Storage Products | TITAN Containers",
    },
    description: {
      id: "Lihat lini produk TITAN Containers: ArcticStore, UltraFreezer, ArcticBlast, dan Arctic SuperStore untuk kebutuhan pangan, farmasi, dan industri.",
      en: "Explore the TITAN Containers range: ArcticStore, UltraFreezer, ArcticBlast and Arctic SuperStore for food, pharma and industrial needs.",
    },
  },
  {
    key: "product-arcticstore",
    path: "product/arcticstore",
    prerender: true,
    product: {
      name: "ArcticStore",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/f_auto,q_auto,w_1200/v1753458902/ArcticStore_Container_without_bg_gwre0q.png",
    },
    title: {
      id: "ArcticStore - Cold Storage Portabel hingga -40°C | TITAN Containers",
      en: "ArcticStore - Portable Cold Storage to -40°C | TITAN Containers",
    },
    description: {
      id: "ArcticStore adalah unit cold storage portabel berbentuk kontainer untuk produk bersuhu hingga -40°C.",
      en: "ArcticStore is a container-shaped portable cold storage unit for products at temperatures down to -40°C.",
    },
  },
  {
    key: "product-ultrafreezer",
    path: "product/ultrafreezer",
    prerender: true,
    product: {
      name: "UltraFreezer",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/f_auto,q_auto,w_1200/v1753458896/Arcticstore_Ultrafreezer_without_bg_kcwxaq.png",
    },
    title: {
      id: "UltraFreezer - Cold Storage Portabel hingga -70°C | TITAN Containers",
      en: "UltraFreezer - Portable Cold Storage to -70°C | TITAN Containers",
    },
    description: {
      id: "UltraFreezer adalah unit cold storage portabel untuk produk yang membutuhkan suhu ekstrem hingga -70°C.",
      en: "UltraFreezer is a portable cold storage unit for products that need extreme temperatures down to -70°C.",
    },
  },
  {
    key: "product-arcticblast",
    path: "product/arcticblast",
    prerender: true,
    product: {
      name: "ArcticBlast",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/f_auto,q_auto,w_1200/v1753458926/Foto_ABF_40_v12pb9.png",
    },
    title: {
      id: "ArcticBlast - Blast Freezer Portabel hingga -40°C | TITAN Containers",
      en: "ArcticBlast - Portable Blast Freezer to -40°C | TITAN Containers",
    },
    description: {
      id: "ArcticBlast adalah blast freezer portabel untuk pendinginan cepat hingga -40°C.",
      en: "ArcticBlast is a portable blast freezer for rapid cooling down to -40°C.",
    },
  },
  {
    key: "product-arcticsuperstore",
    path: "product/arcticsuperstore",
    prerender: true,
    product: {
      name: "Arctic SuperStore",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/f_auto,q_auto,w_1200/v1753458907/Arctic_SuperStore_yaacsr.png",
    },
    title: {
      id: "Arctic SuperStore - Cold Storage Multi-Unit | TITAN Containers",
      en: "Arctic SuperStore - Multi-Unit Cold Storage | TITAN Containers",
    },
    description: {
      id: "Arctic SuperStore menjawab keterbatasan kapasitas cold storage berbentuk kontainer dengan solusi multi-unit portabel dari TITAN Containers.",
      en: "Arctic SuperStore overcomes the capacity limits of container-shaped cold storage with a portable multi-unit solution from TITAN Containers.",
    },
  },
  {
    key: "services",
    path: "our-service",
    prerender: true,
    title: {
      id: "Layanan Beli & Sewa Cold Storage | TITAN Containers",
      en: "Buy & Rent Cold Storage Services | TITAN Containers",
    },
    description: {
      id: "Beli unit cold storage portabel untuk investasi jangka panjang, atau sewa secara fleksibel sesuai kebutuhan proyek dan musim bisnis Anda.",
      en: "Buy a portable cold storage unit as a long-term investment, or rent flexibly to match your project and seasonal business needs.",
    },
  },
  {
    key: "service-buy",
    path: "service/buy",
    prerender: true,
    title: {
      id: "Beli Container Cold Storage | TITAN Containers",
      en: "Buy a Cold Storage Container | TITAN Containers",
    },
    description: {
      id: "Investasi unit cold storage portabel berkualitas: kepemilikan penuh, dapat dikustomisasi, dan menjadi aset perusahaan Anda.",
      en: "Invest in high-quality portable cold storage: full ownership, customizable to your needs, and an asset on your company's balance sheet.",
    },
  },
  {
    key: "service-rent",
    path: "service/rent",
    prerender: true,
    title: {
      id: "Sewa Container Cold Storage | TITAN Containers",
      en: "Rent a Cold Storage Container | TITAN Containers",
    },
    description: {
      id: "Sewa unit cold storage portabel secara fleksibel untuk proyek, musim puncak, atau kebutuhan jangka pendek tanpa investasi awal besar.",
      en: "Rent portable cold storage flexibly for projects, peak seasons or short-term needs, without a large upfront investment.",
    },
  },
  {
    key: "contact",
    path: "contact-us",
    prerender: true,
    title: {
      id: "Hubungi Kami | TITAN Containers",
      en: "Contact Us | TITAN Containers",
    },
    description: {
      id: "Hubungi TITAN Containers di The Kensington Office Tower, Kelapa Gading, Jakarta Utara. Telepon +62 21 4064 4888 atau kirim permintaan penawaran.",
      en: "Contact TITAN Containers at The Kensington Office Tower, Kelapa Gading, North Jakarta. Call +62 21 4064 4888 or send a quote request.",
    },
  },
  {
    key: "get-started",
    path: "get-started",
    prerender: true,
    title: {
      id: "Mulai Sekarang | TITAN Containers",
      en: "Get Started | TITAN Containers",
    },
    description: {
      id: "Mulai solusi cold storage portabel Anda bersama TITAN Containers. Hubungi tim kami untuk konsultasi dan penawaran.",
      en: "Start your portable cold storage solution with TITAN Containers. Talk to our team for a consultation and quote.",
    },
  },
  {
    key: "blogs",
    path: "blogs",
    prerender: true,
    title: {
      id: "Blog & Artikel | TITAN Containers",
      en: "Blog & Articles | TITAN Containers",
    },
    description: {
      id: "Artikel dan wawasan seputar cold storage portabel, rantai dingin, dan penyimpanan suhu terkendali dari TITAN Containers.",
      en: "Articles and insights on portable cold storage, the cold chain and temperature-controlled storage from TITAN Containers.",
    },
  },
  {
    // Article pages are loaded from the API at runtime, so they get this
    // generic metadata (not prerendered, indexable).
    key: "article",
    path: "article/:id",
    prerender: false,
    title: {
      id: "Artikel | TITAN Containers",
      en: "Article | TITAN Containers",
    },
    description: {
      id: "Baca artikel dari TITAN Containers seputar cold storage portabel dan rantai dingin.",
      en: "Read an article from TITAN Containers on portable cold storage and the cold chain.",
    },
  },
  {
    key: "login",
    path: "login",
    prerender: false,
    noindex: true,
    title: { id: "Masuk | TITAN Containers", en: "Sign In | TITAN Containers" },
    description: {
      id: "Masuk ke akun TITAN Containers.",
      en: "Sign in to your TITAN Containers account.",
    },
  },
];

/** Pages that go into sitemap.xml and the prerender step. */
export const INDEXABLE_PAGES = PAGES.filter((p) => !p.noindex && p.prerender);
